import type { Avatar } from '@/utils/types';
import extractIdFromURI from '@/utils/id';
import type { AgendaHierarchyBuildResult } from '@/components/agenda/types';

/** Resuelve el id local del avatar desde referencia API (objeto, @id o URI string). */
export function resolveAvatarId(ref: unknown): string | null {
    if (ref == null) return null;
    if (typeof ref === 'string') return extractIdFromURI(ref);
    if (typeof ref === 'object') {
        const o = ref as Record<string, unknown>;
        if (typeof o.id === 'string' && o.id.length > 0) return extractIdFromURI(o.id);
        const uri = o['@id'];
        if (typeof uri === 'string') return extractIdFromURI(uri);
    }
    return null;
}

/** Índice por id resuelto para enlazar respuestas de listas separadas. */
export function indexAvatarsByResolvedId(avatars: Avatar[] | undefined): Map<string, Avatar> {
    const m = new Map<string, Avatar>();
    if (!avatars) return m;
    for (const a of avatars) {
        const key = resolveAvatarId(a) ?? extractIdFromURI(a.id);
        if (key) m.set(key, a);
    }
    return m;
}

/**
 * Bloque 5 (unión): combina resultados de las 4 requests previas.
 * Cadena: intervention.parent → place → site → client.
 */
export function mergeAgendaEntities(
    interventions: Avatar[],
    places: Avatar[] | undefined,
    sites: Avatar[] | undefined,
    clients: Avatar[] | undefined,
): AgendaHierarchyBuildResult {
    const placesById = indexAvatarsByResolvedId(places);
    const sitesById = indexAvatarsByResolvedId(sites);
    const clientsById = indexAvatarsByResolvedId(clients);

    const placeIds = new Set<string>();
    const siteIds = new Set<string>();
    const clientIds = new Set<string>();
    const byInterventionId: AgendaHierarchyBuildResult['byInterventionId'] = {};

    for (const intervention of interventions) {
        const placeId = resolveAvatarId(intervention.parent);
        const place = placeId ? placesById.get(placeId) : undefined;
        const siteId = place ? resolveAvatarId(place.parent) : null;
        const site = siteId ? sitesById.get(siteId) : undefined;
        const clientId = site ? resolveAvatarId(site.parent) : null;

        if (placeId) placeIds.add(placeId);
        if (siteId) siteIds.add(siteId);
        if (clientId) clientIds.add(clientId);

        byInterventionId[intervention.id] = {
            placeId,
            siteId,
            clientId,
        };
    }

    return {
        placeIds: [...placeIds],
        siteIds: [...siteIds],
        clientIds: [...clientIds],
        placesById,
        sitesById,
        clientsById,
        byInterventionId,
    };
}

/** Ids únicos de parents (place) a partir de intervenciones. */
export function collectPlaceIdsFromInterventions(interventions: Avatar[] | undefined): string[] {
    const s = new Set<string>();
    for (const i of interventions ?? []) {
        const id = resolveAvatarId(i.parent);
        if (id) s.add(id);
    }
    return [...s];
}

/** Ids únicos de parents (site) a partir de places. */
export function collectSiteIdsFromPlaces(places: Avatar[] | undefined): string[] {
    const s = new Set<string>();
    for (const p of places ?? []) {
        const id = resolveAvatarId(p.parent);
        if (id) s.add(id);
    }
    return [...s];
}

/** Ids únicos de parents (client) a partir de sites. */
export function collectClientIdsFromSites(sites: Avatar[] | undefined): string[] {
    const s = new Set<string>();
    for (const si of sites ?? []) {
        const id = resolveAvatarId(si.parent);
        if (id) s.add(id);
    }
    return [...s];
}
